import { forwardRef } from 'react';
import type { PrintFormat, Invoice } from '@/types';
import type { TokenContext } from '@/shared/utils/dataTokens';
import { MM_TO_PX, paperDimensions } from '@/shared/utils/paperSizes';
import { tableAutoHeight } from '@/shared/utils/elementFactory';
import { ElementRenderer } from './ElementRenderer';
import { WatermarkLayer } from './WatermarkLayer';

interface PrintFormatRendererProps {
  printFormat: PrintFormat;
  tokenCtx: TokenContext;
}

export const PrintFormatRenderer = forwardRef<
  HTMLDivElement,
  PrintFormatRendererProps
>(({ printFormat, tokenCtx }, ref) => {
  const dims = paperDimensions(
    printFormat.paper.size,
    printFormat.paper.orientation,
    printFormat.paper.customWidthMm,
    printFormat.paper.customHeightMm,
  );
  const widthPx = dims.width * MM_TO_PX;
  const heightPx = dims.height * MM_TO_PX;

  // ── Pagination ────────────────────────────────────────────────────────────
  // The line-item source the table renders from (details first, then invoice
  // items — mirrors TableRenderer's own selection). We paginate over this list
  // and hand each page a ctx sliced to just its rows; the table itself pads
  // short pages up to itemsPerPage (via the itemsPerPage prop). A missing/zero
  // itemsPerPage means a single page.
  const itemsPerPage =
    printFormat.paper.itemsPerPage && printFormat.paper.itemsPerPage > 0
      ? printFormat.paper.itemsPerPage
      : undefined;

  const itemSource: 'details' | 'invoice' | null =
    tokenCtx.details && tokenCtx.details.length > 0
      ? 'details'
      : tokenCtx.invoice && tokenCtx.invoice.items.length > 0
        ? 'invoice'
        : null;

  const allItems =
    itemSource === 'details'
      ? tokenCtx.details!
      : itemSource === 'invoice'
        ? tokenCtx.invoice!.items
        : [];

  const pageCount = itemsPerPage
    ? Math.max(1, Math.ceil(allItems.length / itemsPerPage))
    : 1;

  // Build the per-page token context with its slice of line items.
  const ctxForPage = (page: number): TokenContext => {
    if (!itemsPerPage) return tokenCtx;
    const start = page * itemsPerPage;
    const slice = allItems.slice(start, start + itemsPerPage);
    if (itemSource === 'details') {
      return { ...tokenCtx, details: slice as TokenContext['details'] };
    }
    if (itemSource === 'invoice') {
      return {
        ...tokenCtx,
        invoice: { ...tokenCtx.invoice!, items: slice as Invoice['items'] },
      };
    }
    return tokenCtx;
  };

  return (
    <div ref={ref}>
      <style>{`
          @page { size: ${dims.width}mm ${dims.height}mm; margin: 0; }
          /* The print path runs with react-to-print's ignoreGlobalStyles, so
             Tailwind Preflight never reaches the iframe. Replicate the two base
             normalizations every on-screen element inherits — without them the
             iframe defaults to content-box + line-height:normal, so table rows
             render a few px shorter than the designer canvas and a cell table
             placed flush below the item table shows a gap in print. */
          .pf-page, .pf-page *, .pf-page *::before, .pf-page *::after {
            box-sizing: border-box;
          }
          /* Match .bf-designer's line-height (src/assets/style/bill-format-designer.css)
             so a row's text occupies the exact same height in print as on the
             designer canvas — otherwise the item table renders a few px shorter
             (iframe default line-height:normal) and a cell table placed flush
             below it shows a gap. */
          .pf-page { line-height: 1.45; }
          /* Force a fresh printed sheet after every page except the last.
             Stylesheet rules survive react-to-print's clone-into-iframe path
             far more reliably than inline break-* styles do. */
          .pf-page { break-after: page; page-break-after: always; }
          .pf-page:last-child { break-after: auto; page-break-after: auto; }
          /* Keep background-based graphics (solid lines, header tints, shape
             fills) when printing — browsers drop them by default. */
          .pf-page, .pf-page * {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        `}</style>
      {Array.from({ length: pageCount }, (_, page) => {
        const pageCtx = ctxForPage(page);
        return (
          <div
            key={page}
            className="pf-page"
            style={{
              position: 'relative',
              width: `${widthPx}px`,
              height: `${heightPx}px`,
              backgroundColor: printFormat.background ?? '#ffffff',
              overflow: 'hidden',
            }}
          >
            {/* Page-wide watermark, stamped behind the elements. Tokens in the
                text resolve against this page's context. */}
            <WatermarkLayer
              watermark={printFormat.watermark}
              widthPx={widthPx}
              heightPx={heightPx}
              zoom={1}
              fallbackFontFamily={printFormat.defaultFontFamily}
              tokenCtx={pageCtx}
            />

            {[...printFormat.elements]
              .filter((el) => !el.hidden)
              .sort((a, b) => a.position.zIndex - b.position.zIndex)
              .map((el) => {
                // Self-sizing tables shrink-wrap to their rows, so the wrapper
                // height is left to the content instead of the stored (possibly
                // stale) element height — no empty space below. Item tables skip
                // the legacy "divide evenly" mode; cell tables always self-size.
                const selfSizes =
                  (el.type === 'table' && tableAutoHeight(el, itemsPerPage)) ||
                  el.type === 'cellTable' ||
                  el.type === 'voucherTable';
                return (
                <div
                  key={el.id}
                  style={{
                    position: 'absolute',
                    left: `${el.position.x * MM_TO_PX}px`,
                    top: `${el.position.y * MM_TO_PX}px`,
                    width: `${el.position.width * MM_TO_PX}px`,
                    height: selfSizes
                      ? 'auto'
                      : `${el.position.height * MM_TO_PX}px`,
                    transform: `rotate(${el.position.rotation}deg)`,
                    opacity: el.opacity,
                    zIndex: el.position.zIndex,
                  }}
                >
                  <ElementRenderer
                    el={el}
                    ctx={pageCtx}
                    printMode={true}
                    itemsPerPage={itemsPerPage}
                    currencyPrefix={printFormat.currencyPrefix}
                  />
                </div>
                );
              })}
          </div>
        );
      })}
    </div>
  );
});

PrintFormatRenderer.displayName = 'PrintFormatRenderer';
